import {
  Badge,
  Card,
  cx,
  ErrorBox,
  Field,
  Input,
  Note,
  Page,
  Stat,
  Table,
  Td,
  useSearch,
} from '@elsa/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { daysLeft, fmtDate, fmtLei, fmtMinor, fmtNum, STATUS_LABEL } from '../lib/format.ts';
import { useOrg } from '../lib/session.tsx';
import { app, callFunction, must, rpc } from '../lib/supabase.ts';
import type { Entitlements, Plan } from '../lib/types.ts';
import { BusyButton, SkeletonCards, SkeletonForm, SkeletonTable, Spinner } from './loading.tsx';

// Abonamentul firmei (docs/22 §3): planul curent, planurile publice, Stripe Checkout (prima plata)
// si Customer Portal (card, facturi, schimbare, anulare). Starea vine din webhook-ul Stripe,
// deci dupa plata pagina asteapta cateva secunde confirmarea.

const FEATURE: Record<string, (v: unknown) => string> = {
  seats: (v) =>
    v === undefined
      ? 'utilizatori nelimitați'
      : `${v} ${Number(v) === 1 ? 'utilizator' : 'utilizatori'}`,
  history_days: (v) => (v === undefined ? 'tot istoricul' : `istoric: ultimele ${v} de zile`),
  max_watchlists: (v) => (v === undefined ? 'alerte nelimitate' : `${v} alerte`),
  export_rows_month: (v) =>
    v === undefined ? 'exporturi nelimitate' : `${fmtNum(v as number)} rânduri exportate pe lună`,
  cpv_codes: (v) => (v === undefined ? 'coduri CPV nelimitate' : `${v} coduri CPV urmărite`),
};

export function features(f: Entitlements['features']): string[] {
  return ['cpv_codes', 'seats', 'history_days', 'max_watchlists', 'export_rows_month'].map((k) =>
    (FEATURE[k] as (v: unknown) => string)(f[k as keyof typeof f]),
  );
}

interface Overview {
  entitlements: Entitlements;
  invoices: {
    id: string;
    status: string;
    currency: string;
    amount_due: number | null;
    amount_paid: number | null;
    created_at: string;
    hosted_invoice_url: string | null;
    invoice_pdf: string | null;
    fiscal_invoice_number: string | null;
  }[];
}

export function CompanyForm() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['organization', org.id],
    queryFn: async () =>
      (
        (await must(app().from('organizations').select('*').eq('id', org.id))) as Record<
          string,
          string | null
        >[]
      )[0],
  });
  const save = useMutation({
    mutationFn: (patch: Record<string, string | null>) =>
      must(app().from('organizations').update(patch).eq('id', org.id)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization'] });
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
  if (!q.data) return <SkeletonForm fields={6} />;
  const o = q.data;
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const v = (k: string) => {
      const x = String(f.get(k) ?? '').trim();
      return x ? x : null;
    };
    save.mutate({
      name: v('name') ?? o.name ?? null,
      reg_com: v('reg_com'),
      address: v('address'),
      county: v('county'),
      city: v('city'),
      billing_email: v('billing_email'),
    });
  };
  return (
    <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
      <Field label="Denumirea">
        <Input name="name" defaultValue={o.name ?? ''} required />
      </Field>
      <Field label="CUI" help="Nu se schimbă.">
        <Input value={o.cui ?? ''} disabled readOnly />
      </Field>
      <Field label="Nr. Registrul Comerțului">
        <Input name="reg_com" defaultValue={o.reg_com ?? ''} />
      </Field>
      <Field label="Email pentru facturi">
        <Input name="billing_email" type="email" defaultValue={o.billing_email ?? ''} />
      </Field>
      <Field label="Adresa">
        <Input name="address" defaultValue={o.address ?? ''} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Județul">
          <Input name="county" defaultValue={o.county ?? ''} />
        </Field>
        <Field label="Localitatea">
          <Input name="city" defaultValue={o.city ?? ''} />
        </Field>
      </div>
      <div className="sm:col-span-2 flex items-center gap-3">
        <BusyButton type="submit" busy={save.isPending}>
          Salvează datele firmei
        </BusyButton>
        {save.isSuccess ? <span className="text-xs text-gri-700">Salvat.</span> : null}
        <ErrorBox error={save.error} />
      </div>
    </form>
  );
}

export function BillingPage() {
  const { org, can, entitlements } = useOrg();
  const qc = useQueryClient();
  const sp = useSearch();
  const paid = sp.get('plata') === 'ok';
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const manage = can('billing.manage');
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => rpc<Plan[]>('plans') });
  const overview = useQuery({
    queryKey: ['billing_overview', org.id],
    enabled: manage,
    queryFn: () => rpc<Overview>('billing_overview', { p_org: org.id }),
    // Dupa Checkout, webhook-ul Stripe poate intarzia cateva secunde.
    refetchInterval: (q) => (paid && q.state.data?.entitlements.plan === 'trial' ? 2000 : false),
  });
  const ent = overview.data?.entitlements ?? entitlements;
  useEffect(() => {
    if (paid && ent?.plan && ent.plan !== 'trial')
      void qc.invalidateQueries({ queryKey: ['entitlements'] });
  }, [paid, ent?.plan, qc]);
  const checkout = useMutation({
    mutationFn: (plan: string) =>
      callFunction<{ url: string }>('billing', {
        action: 'checkout',
        organization_id: org.id,
        plan,
        interval,
      }),
    onSuccess: (r) => location.assign(r.url),
  });
  const portal = useMutation({
    mutationFn: () =>
      callFunction<{ url: string }>('billing', { action: 'portal', organization_id: org.id }),
    onSuccess: (r) => location.assign(r.url),
  });

  const sub = ent?.subscription;
  const isTrial = ent?.plan === 'trial';
  // Acces acordat de echipa Elsa (din Pluto): nu e plata, deci nu are portal; se poate cumpara.
  const isManual = sub?.kind === 'manual';
  const hasPaid = Boolean(ent?.plan && !isTrial && !isManual);
  const current = plans.data?.find((p) => p.id === ent?.plan);
  return (
    <Page
      title="Abonament"
      actions={
        manage && hasPaid ? (
          <BusyButton tone="primary" busy={portal.isPending} onClick={() => portal.mutate()}>
            Gestionează plata și facturile
          </BusyButton>
        ) : null
      }
    >
      {paid && isTrial ? (
        <Note>
          <span className="inline-flex items-center gap-2">
            <Spinner />
            Plata se confirmă (câteva secunde)…
          </span>
        </Note>
      ) : null}
      {paid && hasPaid ? <Note>Mulțumim! Abonamentul e activ.</Note> : null}
      {sp.get('plata') === 'anulata' ? (
        <Note>Plata a fost anulată. Nu s-a încasat nimic.</Note>
      ) : null}
      <ErrorBox error={checkout.error ?? portal.error ?? plans.error} />
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat
          label="Planul"
          value={isTrial ? 'Perioadă de probă' : (current?.name ?? ent?.plan ?? 'fără plan')}
          sub={
            isManual
              ? 'acordat de echipa Elsa'
              : sub
                ? (STATUS_LABEL[sub.status] ?? sub.status)
                : undefined
          }
        />
        <Stat
          label={
            isTrial
              ? 'Proba se termină'
              : isManual
                ? 'Acces până la'
                : sub?.cancel_at_period_end
                  ? 'Se încheie la'
                  : 'Următoarea plată'
          }
          value={
            isManual && !sub?.current_period_end
              ? 'fără termen'
              : fmtDate(isTrial ? sub?.trial_end : sub?.current_period_end)
          }
          sub={isTrial ? `${daysLeft(sub?.trial_end)} zile rămase` : undefined}
        />
        <Stat
          label="Utilizatori"
          value={fmtNum(ent?.members)}
          sub={ent ? features(ent.features)[0] : undefined}
        />
      </div>
      {ent?.plan ? (
        <p className="text-sm text-gri-800">
          Planul curent include: {features(ent.features).join(' · ')}.
        </p>
      ) : null}
      {!manage ? (
        <Note>Abonamentul îl gestionează proprietarul sau un administrator al firmei.</Note>
      ) : !hasPaid ? (
        <Card
          title="Alege un plan"
          actions={
            <div className="flex border-2 border-cerneala text-sm font-bold">
              {(['month', 'year'] as const).map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setInterval(i)}
                  className={cx(
                    'px-3 py-1',
                    interval === i ? 'bg-cerneala text-hartie' : 'hover:bg-cerneala/5',
                  )}
                >
                  {i === 'month' ? 'Lunar' : 'Anual'}
                </button>
              ))}
            </div>
          }
        >
          {plans.isLoading ? <SkeletonCards count={3} lines={4} /> : null}
          {plans.data && !plans.data.length ? (
            <p className="text-sm text-gri-700">
              Planurile se anunță în curând. Între timp, folosește perioada de probă.
            </p>
          ) : null}
          <div className="grid gap-3 md:grid-cols-3">
            {plans.data?.map((p) => {
              const price = interval === 'month' ? p.price_monthly_ron : p.price_yearly_ron;
              const available = interval === 'month' ? p.monthly : p.yearly;
              return (
                <div
                  key={p.id}
                  className="flex flex-col justify-between gap-3 border-2 border-cerneala bg-hartie p-4"
                >
                  <div>
                    <div className="text-lg font-extrabold">{p.name}</div>
                    {p.description ? <p className="text-sm text-gri-700">{p.description}</p> : null}
                    <div className="mt-2 text-[28px] leading-none font-extrabold tracking-tight tabular-nums">
                      {price === null ? '—' : fmtLei(price)}
                      <span className="text-sm font-normal text-gri-700">
                        {' '}
                        / {interval === 'month' ? 'lună' : 'an'} + TVA
                      </span>
                    </div>
                    <ul className="mt-2 space-y-0.5 text-sm text-gri-800">
                      {features(p.features).map((x) => (
                        <li key={x}>✓ {x}</li>
                      ))}
                    </ul>
                  </div>
                  <BusyButton
                    tone="primary"
                    busy={checkout.isPending && checkout.variables === p.id}
                    disabled={!available || checkout.isPending}
                    onClick={() => checkout.mutate(p.id)}
                  >
                    {available ? `Alege ${p.name}` : 'Indisponibil pe acest interval'}
                  </BusyButton>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-gri-700">
            Plata cu cardul prin Stripe. Abonamentul se reînnoiește automat; îl anulezi oricând din
            portalul de plată.
          </p>
        </Card>
      ) : null}
      {manage ? (
        <>
          <Card title="Facturi">
            {overview.isLoading ? (
              <SkeletonTable rows={3} cols={5} />
            ) : (
              <Table
                head={['Data', 'Suma', 'Starea', 'Factura fiscală', '']}
                empty={!overview.data?.invoices.length}
              >
                {overview.data?.invoices.map((i) => (
                  <tr key={i.id}>
                    <Td>{fmtDate(i.created_at)}</Td>
                    <Td className="tabular-nums">{fmtMinor(i.amount_due, i.currency)}</Td>
                    <Td>
                      <Badge
                        tone={
                          i.status === 'paid' ? 'good' : i.status === 'open' ? 'warn' : 'neutral'
                        }
                      >
                        {i.status === 'paid'
                          ? 'plătită'
                          : i.status === 'open'
                            ? 'de plată'
                            : i.status}
                      </Badge>
                    </Td>
                    <Td>{i.fiscal_invoice_number ?? '—'}</Td>
                    <Td>
                      {i.hosted_invoice_url ? (
                        <a
                          href={i.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm underline"
                        >
                          Chitanța
                        </a>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </Table>
            )}
          </Card>
          <Card title="Datele firmei">
            <CompanyForm />
          </Card>
        </>
      ) : null}
    </Page>
  );
}
